import React, { useCallback } from 'react';

import classnames from 'clsx';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { noop } from 'ui-components/default-props/noop';

import { CountryDropdownMenuItem } from './country-dropdown-menu-item';
import { useSortedTranslatedCountries } from '../../hooks/use-sorted-translated-countries';
import { MessageDescriptorPropType } from '../../prop-types/intl';
import { messages } from './messages';

import style from './country-dropdown.module.less';

export const CountryDropdown = ({
  selectedCountryCode = '',
  onCountrySelected = noop,
  className = '',
  dropdownMenuShouldHaveWidthOfButton = false,
  togglerClassName = '',
  required = false,
  disabled = false,
  variant = 'button',
  defaultTitleMessageDescriptor = messages.defaultTitle,
  menuItemClassName,
  shouldShowDefaultMenuItem = false,
  id,
}) => {
  const intl = useIntl();
  const countries = useSortedTranslatedCountries();
  const defaultTitle = intl.formatMessage(defaultTitleMessageDescriptor);
  const title = countries.find((country) => country.code === selectedCountryCode)?.label ?? defaultTitle;

  const selectDefaultMenuItem = useCallback(() => {
    onCountrySelected(defaultTitle);
  }, [defaultTitle, onCountrySelected]);

  return (
    <DropdownMenu
      className={classnames(style['country-dropdown'], className)}
      disabled={disabled}
      dropdownMenuShouldHaveWidthOfButton={dropdownMenuShouldHaveWidthOfButton}
      dropdownMenuType={DropdownMenuType.ComboBox}
      fullWidth
      id={id}
      variant={variant}
    >
      <DropdownMenuToggler className={togglerClassName} required={required}>
        {title}
      </DropdownMenuToggler>
      {!selectedCountryCode && shouldShowDefaultMenuItem && (
        <DropdownMenuItem onClick={selectDefaultMenuItem}>{defaultTitle}</DropdownMenuItem>
      )}
      {countries.map(({ code, label }) => (
        <CountryDropdownMenuItem
          key={code}
          className={menuItemClassName}
          countryCode={code}
          countryName={label}
          onClick={onCountrySelected}
          selected={selectedCountryCode === code}
        />
      ))}
    </DropdownMenu>
  );
};

CountryDropdown.propTypes = {
  selectedCountryCode: PropTypes.string,
  onCountrySelected: PropTypes.func,
  className: PropTypes.string,
  dropdownMenuShouldHaveWidthOfButton: PropTypes.bool,
  togglerClassName: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['button', 'form']),
  defaultTitleMessageDescriptor: MessageDescriptorPropType,
  menuItemClassName: PropTypes.string,
  shouldShowDefaultMenuItem: PropTypes.bool,
  id: PropTypes.string,
};
